// BMP_QBO_INVOICE_PDF_V1 — Client API, no QuickBooks secrets in the browser.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export type InvoicePdfSyncStatus = {
  quickBooksInvoiceId: string | null;
  status: 'NOT_SYNCED' | 'MISSING' | 'SYNCING' | 'READY' | 'FAILED';
  documentId: string | null;
  fileName: string | null;
  syncedAt: string | null;
  lastError: string | null;
};

export async function syncInvoicePdf(
  orderId: string,
  force = false
): Promise<{
  success: true;
  status: 'READY';
  documentId: string;
  fileName: string;
  alreadySynced: boolean;
}> {
  const { data } = await api.post(
    `/organization/orders/${encodeURIComponent(orderId)}/invoice-pdf/sync`,
    { force }
  );
  return data;
}

export function useInvoicePdfStatus(orderId?: string, enabled = true) {
  return useQuery<InvoicePdfSyncStatus>({
    queryKey: ['org:invoice-pdf', orderId],
    queryFn: async () => {
      const { data } = await api.get(
        `/organization/orders/${encodeURIComponent(orderId!)}/invoice-pdf`
      );
      return data;
    },
    enabled: enabled && Boolean(orderId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useSyncInvoicePdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, force = false }: { orderId: string; force?: boolean }) =>
      syncInvoicePdf(orderId, force),
    onSettled: (_result, _error, vars) => {
      if (!vars) return;
      void queryClient.invalidateQueries({ queryKey: ['org:invoice-pdf', vars.orderId] });
      void queryClient.invalidateQueries({ queryKey: ['order-documents', vars.orderId] });
    },
  });
}
